import React, { useEffect, useState } from "react";
import { useDownload } from "@/hooks/use-download";
import {
  getNormalizedTitle,
  getStemTypeFromTitle,
} from "@/utils/history-helper";
import styles from "@/components/modal/result-modal/styles/result-modal.module.scss";
import { ToastContainer } from "react-toastify";
import CoverTab from "@/components/modal/result-modal/components/cover-tab";
import MusicPlayer from "@/components/pages/convert/components/music-player";
import ShareCoverButton from "@/components/common/share-cover-button";
import DownloadButton from "@/components/modal/result-modal/components/download-button";
import { useGeneratedSongUrl } from "@/hooks/use-generated-song-url";
import { useProfileContext } from "@/providers/profile-provider/profile-provider";
import { useLoggingContext } from "@/providers/logging-provider/logging-provider";

type StemResultModalProps = {
  guitarFileName?: string;
  pianoFileName?: string;
  drumsFileName?: string;
  bassFileName?: string;
  vocalsFileName?: string;
  othersFileName?: string;
  icon?: string;
  format?: string;
  onSongsDownloaded: () => void;
  isOpen: boolean;
  splitId: string;
};

const StemResultModal = ({
  guitarFileName,
  pianoFileName,
  drumsFileName,
  bassFileName,
  vocalsFileName,
  othersFileName,
  icon,
  format,
  onSongsDownloaded,
  splitId,
}: StemResultModalProps) => {
  const logger = useLoggingContext();

  const { license, isLoading } = useProfileContext();
  const getFirstAvailableFileName = () => {
    return (
      guitarFileName ??
      pianoFileName ??
      drumsFileName ??
      bassFileName ??
      vocalsFileName ??
      othersFileName ??
      ""
    );
  };

  const getFileUrl = (fileName: string) => {
    switch (fileName) {
      case guitarFileName:
        return guitarFileUrl;
      case pianoFileName:
        return pianoFileUrl;
      case drumsFileName:
        return drumsFileUrl;
      case bassFileName:
        return bassFileUrl;
      case vocalsFileName:
        return vocalsFileUrl;
      case othersFileName:
        return othersFileUrl;
      default:
        return "";
    }
  };

  const getStemTypeByFilename = (filename: string) => {
    try {
      return getStemTypeFromTitle(filename);
    } catch (err) {
      logger.logError(err as Error);
      return "Other";
    }
  };

  const [selectedFileUrl, setSelectedFileUrl] = useState("");
  const [selectedFileName, setSelectedFileName] = useState(
    getFirstAvailableFileName,
  );
  const [guitarFileUrl, setGuitarFileUrl] = useState("");
  const [pianoFileUrl, setPianoFileUrl] = useState("");
  const [drumsFileUrl, setDrumsFileUrl] = useState("");
  const [bassFileUrl, setBassFileUrl] = useState("");
  const [vocalsFileUrl, setVocalsFileUrl] = useState("");
  const [othersFileUrl, setOthersFileUrl] = useState("");
  const [activeTab, setActiveTab] = useState(
    getStemTypeByFilename(getFirstAvailableFileName()),
  );

  const { download } = useDownload();
  const { getFileUrlByName: getFile } = useGeneratedSongUrl("/api/split/get?");

  const getAndSetFileAsync = (
    fileName: string | undefined,
    setCallback: (url: string) => void,
  ) => {
    if (fileName) {
      getFile(fileName).then((res) => {
        if (res) {
          setCallback(res);
        }
      });
    }
  };

  useEffect(() => {
    const defaultFileName = getFirstAvailableFileName();
    const defaultFileUrl = getFileUrl(defaultFileName);

    if (defaultFileName && defaultFileUrl) {
      onTabClick(defaultFileName, defaultFileUrl);
      setSelectedFileName(defaultFileName);
      setActiveTab(getStemTypeByFilename(defaultFileName));
    }
  }, [
    guitarFileName,
    pianoFileName,
    drumsFileName,
    bassFileName,
    vocalsFileName,
    othersFileName,
    guitarFileUrl,
    pianoFileUrl,
    drumsFileUrl,
    bassFileUrl,
    vocalsFileUrl,
    othersFileUrl,
  ]);

  useEffect(() => {
    getAndSetFileAsync(guitarFileName, setGuitarFileUrl);
    getAndSetFileAsync(pianoFileName, setPianoFileUrl);
    getAndSetFileAsync(drumsFileName, setDrumsFileUrl);
    getAndSetFileAsync(bassFileName, setBassFileUrl);
    getAndSetFileAsync(vocalsFileName, setVocalsFileUrl);
    getAndSetFileAsync(othersFileName, setOthersFileUrl);
    onSongsDownloaded();
  }, [
    guitarFileName,
    pianoFileName,
    drumsFileName,
    bassFileName,
    vocalsFileName,
    othersFileName,
    onSongsDownloaded,
  ]);

  const onTabClick = (fileName: string, fileUrl: string) => {
    setSelectedFileName(fileName);
    setSelectedFileUrl(fileUrl);
    setActiveTab(getStemTypeByFilename(fileName));
  };

  return (
    <div className={styles.modal}>
      <div className={styles.header}>
        <div className={styles.header_text}>Splitter results</div>
      </div>
      <ToastContainer position="top-right" />
      <div className={styles.tabs_container}>
        {guitarFileName && (
          <CoverTab
            title="Guitar"
            fileName={guitarFileName}
            fileUrl={guitarFileUrl}
            activeTab={activeTab}
            onClick={onTabClick}
          />
        )}
        {pianoFileName && (
          <CoverTab
            title="Piano"
            fileName={pianoFileName}
            fileUrl={pianoFileUrl}
            activeTab={activeTab}
            onClick={onTabClick}
          />
        )}
        {drumsFileName && (
          <CoverTab
            title="Drums"
            fileName={drumsFileName}
            fileUrl={drumsFileUrl}
            activeTab={activeTab}
            onClick={onTabClick}
          />
        )}
        {bassFileName && (
          <CoverTab
            title="Bass"
            fileName={bassFileName}
            fileUrl={bassFileUrl}
            activeTab={activeTab}
            onClick={onTabClick}
          />
        )}
        {vocalsFileName && (
          <CoverTab
            title="Vocals"
            fileName={vocalsFileName}
            fileUrl={vocalsFileUrl}
            activeTab={activeTab}
            onClick={onTabClick}
          />
        )}
        {othersFileName && (
          <CoverTab
            title="Other"
            fileName={othersFileName}
            fileUrl={othersFileUrl}
            activeTab={activeTab}
            onClick={onTabClick}
          />
        )}
      </div>
      <MusicPlayer
        title={getNormalizedTitle(selectedFileName)}
        url={selectedFileUrl}
        format={format}
        icon={icon}
        stickyProgress={true}
      />
      {license !== "Trial" && !isLoading && (
        <div className={styles.footer}>
          <DownloadButton
            onClick={() =>
              download(selectedFileUrl, getNormalizedTitle(selectedFileName))
            }
          />
        </div>
      )}
    </div>
  );
};

export default StemResultModal;
